import React, { useState, useMemo, useEffect } from "react";
import { Loader2, Search, MapPin, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Hash, Building2, Package as PackageIcon, Mail, Phone, Download, Trophy, TriangleAlert, Medal, FileText, Eye } from "lucide-react";
import { useAdminConferenceRooms, type ConferenceRoom } from "@/services/adminConferenceRoomService";
import { useAdminRoomAttendances, useAdminTrackAttendances, useTogglePresence, useToggleBestPaper, useToggleBestPresenter, useUpdateScores, useAutoRankBestPaper, useAutoRankBestPresenter, exportConferenceAttendancesExcel } from "@/services/adminConferenceAttendanceService";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { usePeriods } from "@/services/usePeriods";
import { useUser } from "@/context/useUser";
import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import { cn } from "@/lib/utils";
import toast from "react-hot-toast";

export default function AttendancePage() {
    const { data: periods } = usePeriods();
    const activePeriod = periods?.find((p: any) => p.is_active);

    const { data: currentUser } = useUser();
    const roleId = currentUser?.role_id;
    const isPrivilegedRole = roleId === 1 || roleId === 4;
    const isPetugasPresensi = roleId === 5;

    const [filterPeriod, setFilterPeriod] = useState<string>("all");
    const [filterConference, setFilterConference] = useState<string>("all");
    const [filterDate, setFilterDate] = useState<string>("all");
    const [filterRoom, setFilterRoom] = useState<string>("all");
    const [filterSearch, setFilterSearch] = useState<string>("");
    
    // Advanced Filters
    const [filterPresence, setFilterPresence] = useState<string>("all");
    const [filterStatus, setFilterStatus] = useState<string>("all");
    const [filterAchievement, setFilterAchievement] = useState<string>("all");

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);

    // State for expanded rows
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (activePeriod && filterPeriod === "all") {
            setFilterPeriod(String(activePeriod.period_id));
        }
    }, [activePeriod, filterPeriod]);

    const { data: conferences } = useQuery({
        queryKey: ["conferencesList"],
        queryFn: async () => {
            const { data } = await axiosClient.get("/private/conference-billing/accessible-conferences");
            return data.data;
        }
    });

    // Jika Operator Conference (4) atau Moderator/Petugas Presensi (5) hanya punya akses ke 1 track,
    // langsung pilihkan track itu (dropdown "Semua Track" bukan opsi berguna buat mereka), supaya
    // tombol Generate Sertifikat/Hitung Otomatis bisa langsung aktif tanpa perlu klik dropdown dulu.
    useEffect(() => {
        const isOperatorOrModerator = roleId === 4 || roleId === 5;
        if (isOperatorOrModerator && filterConference === "all" && conferences?.length === 1) {
            setFilterConference(String(conferences[0].conference_id));
        }
    }, [roleId, filterConference, conferences]);

    const conferenceIdParam = filterConference !== "all" ? filterConference : undefined;
    const { data: rooms } = useAdminConferenceRooms(conferenceIdParam);

    // Get unique dates based on current filtered rooms
    const uniqueDates = useMemo(() => {
        if (!rooms) return [];
        const filteredRooms = rooms.filter(r => {
            if (filterPeriod !== "all" && String(r.period_id) !== filterPeriod) return false;
            return true;
        });
        const dates = new Set(filteredRooms.map(r => r.date).filter(Boolean));
        return Array.from(dates).sort();
    }, [rooms, filterPeriod]);

    // Available rooms for dropdown
    const availableRooms = useMemo(() => {
        if (!rooms) return [];
        return rooms.filter(r => {
            if (filterPeriod !== "all" && String(r.period_id) !== filterPeriod) return false;
            if (filterDate !== "all" && r.date !== filterDate) return false;
            return true;
        });
    }, [rooms, filterPeriod, filterDate]);

    // When filters change, reset room to 'all' if the selected room is no longer in availableRooms
    useEffect(() => {
        if (filterRoom !== "all") {
            const stillExists = availableRooms.find(r => String(r.id) === filterRoom);
            if (!stillExists) setFilterRoom("all");
        }
    }, [availableRooms, filterRoom]);

    // Petugas Presensi hanya boleh melihat ruangan yang mereka tugaskan (dropdown sudah difilter dari backend),
    // jadi paksa pilih ruangan tersebut karena mereka tidak boleh mengakses tampilan "Semua Ruangan" (level track).
    useEffect(() => {
        if (isPetugasPresensi && filterRoom === "all" && availableRooms.length > 0) {
            setFilterRoom(String(availableRooms[0].id));
        }
    }, [isPetugasPresensi, filterRoom, availableRooms]);

    const { data: attendances, isLoading: isAttendancesLoading } = useAdminRoomAttendances(filterRoom, filterConference);
    const { data: trackAttendances } = useAdminTrackAttendances(filterConference);

    const togglePresence = useTogglePresence();
    const toggleBestPaper = useToggleBestPaper();
    const toggleBestPresenter = useToggleBestPresenter();

    const updateScores = useUpdateScores();
        const autoRankBestPaper = useAutoRankBestPaper();
    const autoRankBestPresenter = useAutoRankBestPresenter();
    const [isAutoRankModalOpen, setIsAutoRankModalOpen] = useState(false);
    const [isWinnersModalOpen, setIsWinnersModalOpen] = useState(false);
    const [winnersData, setWinnersData] = useState<any[]>([]);

    // Generate Certificate states
    const [isGenerating, setIsGenerating] = useState(false);
    const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
    const [generateOutput, setGenerateOutput] = useState<string | null>(null);

    // Preview Certificate states
    // Satu peserta bisa punya lebih dari 1 sertifikat konferensi sekaligus (mis. sebagai
    // Pemakalah DAN sebagai Juara Best Paper), jadi modal preview menyimpan seluruh daftar
    // sertifikat milik registrasi tsb dan admin memilih salah satu untuk ditampilkan.
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewName, setPreviewName] = useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewCerts, setPreviewCerts] = useState<Array<{
        id: number;
        label: string;
        is_file_generated: boolean;
        url: string | null;
    }>>([]);
    const [selectedPreviewCertId, setSelectedPreviewCertId] = useState<number | null>(null);

    // Calculate ties for paper_score (per track)
    const scoreCounts = new Map<number, number>();
    if (filterConference !== "all" && trackAttendances) {
        trackAttendances.forEach(att => {
            if (att.paper_score && att.paper_score > 0) {
                scoreCounts.set(att.paper_score, (scoreCounts.get(att.paper_score) || 0) + 1);
            }
        });
    }

    // Calculate ties for presenter_score (per room)
    const presenterScoreCounts = new Map<string, number>(); // key: roomId_score
    if (filterConference !== "all" && trackAttendances) {
        trackAttendances.forEach(att => {
            if (att.room_id && att.presenter_score && att.presenter_score > 0) {
                const key = `${att.room_id}_${att.presenter_score}`;
                presenterScoreCounts.set(key, (presenterScoreCounts.get(key) || 0) + 1);
            }
        });
    }

    // Calculate Summary Stats
    const stats = useMemo(() => {
        if (!attendances) return { total: 0, present: 0, absent: 0, unscored: 0 };
        let present = 0;
        let unscored = 0;
        attendances.forEach(att => {
            if (att.is_present) present++;
            if (!((att.paper_score != null && att.paper_score > 0) || (att.presenter_score != null && att.presenter_score > 0))) unscored++;
        });
        return {
            total: attendances.length,
            present,
            absent: attendances.length - present,
            unscored
        };
    }, [attendances]);

    // Check if Winners are missing
    const missingWinners = useMemo(() => {
        if (filterConference === "all" || !trackAttendances) return false;
        const hasBestPaper = trackAttendances.some(att => att.is_best_paper);
        
        // check if every room has a best presenter
        const roomsInTrack = new Set(trackAttendances.filter(a => a.room_id).map(a => a.room_id));
        let allRoomsHavePresenter = true;
        for (const roomId of roomsInTrack) {
            if (!trackAttendances.some(a => a.room_id === roomId && a.is_best_presenter)) {
                allRoomsHavePresenter = false;
                break;
            }
        }
        return !hasBestPaper || !allRoomsHavePresenter;
    }, [filterConference, trackAttendances]);


        const handleToggleWinner = async (att: any) => {
        try {
            if (filterRoom === "all") {
                // Best paper
                await toggleBestPaper.mutateAsync({ id: att.id, best_paper_rank: null });
                setWinnersData((prev) => prev.filter((w) => w.id !== att.id));
                toast.success("Berhasil membatalkan status juara Best Paper");
            } else {
                // Best presenter
                await toggleBestPresenter.mutateAsync({ id: att.id, is_best_presenter: false });
                setWinnersData((prev) => prev.filter((w) => w.id !== att.id));
                toast.success("Berhasil membatalkan status juara Best Presenter");
            }
        } catch (error) {
            toast.error("Gagal mengubah status juara");
        }
    };

    const handleAutoRank = async () => {
        if (filterConference === "all") return toast.error("Pilih Track Konferensi terlebih dahulu!");
        try {
            let res;
            if (filterRoom === "all") {
                res = await autoRankBestPaper.mutateAsync(Number(filterConference));
                toast.success("Berhasil menghitung juara Best Paper otomatis!");
            } else {
                res = await autoRankBestPresenter.mutateAsync(Number(filterRoom));
                toast.success("Berhasil menghitung juara Best Presenter otomatis!");
            }
            setIsAutoRankModalOpen(false);
            if (res && res.winners && res.winners.length > 0) {
                setWinnersData(res.winners);
                setIsWinnersModalOpen(true);
            }
        } catch (error) {
            toast.error("Gagal menghitung juara otomatis");
        }
    };

    const [isExporting, setIsExporting] = useState(false);

    const handleExportExcel = async () => {
        if (filterConference === "all") {
            return toast.error("Silakan pilih Track Konferensi terlebih dahulu untuk diexport!");
        }
        if (!attendances || attendances.length === 0) {
            return toast.error("Gagal melakukan export: Tidak ada data partisipan pada track atau ruangan ini.");
        }

        const confId = filterConference;
        const roomId = filterRoom;
        
        try {
            setIsExporting(true);
            await exportConferenceAttendancesExcel(confId, roomId, filterPresence, filterStatus, filterAchievement);
            toast.success("Data berhasil diexport!");
        } catch (error: any) {
            console.error("Export error:", error);
            if (error.response?.data instanceof Blob) {
                // If it's a blob error, we can optionally parse it, but for now a generic message is fine since we pre-checked
                toast.error("Gagal melakukan export: Terjadi kesalahan pada server.");
            } else {
                toast.error("Gagal melakukan export data");
            }
        } finally {
            setIsExporting(false);
        }
    };

    const handleGenerateCertificates = async () => {
        if (filterConference === "all") {
            toast.error("Pilih Track Konferensi terlebih dahulu!");
            return;
        }
        setIsGenerating(true);
        setGenerateOutput(null);
        try {
            const payload: any = { type: 'conference', force: false };
            payload.conference_id = filterConference;
            if (filterRoom !== 'all') payload.room_id = filterRoom;
            const res = await axiosClient.post('/private/certificates/generate-local', payload);
            setGenerateOutput(res.data?.output || res.data?.message || 'Selesai!');
            if (res.data?.generated_count === 0) {
                toast(res.data?.warning || 'Tidak ada sertifikat yang dibuat: tidak ada peserta yang memenuhi syarat.', { icon: <TriangleAlert className="w-5 h-5 text-amber-500" /> });
            } else {
                toast.success('Generate sertifikat berhasil!');
            }
        } catch (error: any) {
            const msg = error.response?.data?.details || error.response?.data?.error || 'Gagal generate sertifikat.';
            setGenerateOutput(msg);
            toast.error('Gagal generate sertifikat.');
        } finally {
            setIsGenerating(false);
        }
    };

    const handlePreviewCertificate = async (att: any) => {
        const name = att.registration?.participant_name || att.registration?.name || 'Peserta';
        setPreviewName(name);
        setPreviewCerts([]);
        setSelectedPreviewCertId(null);
        setIsPreviewModalOpen(true);
        setPreviewLoading(true);
        try {
            const regId = att.registration?.registration_id || att.registration_id;
            // registration_id + type di-scope persis ke peserta ini (lihat fix applyFilters()
            // di CertificateController) - sebelumnya param registration_id diabaikan backend
            // sehingga preview bisa menampilkan sertifikat milik peserta lain.
            const res = await axiosClient.get('/private/certificates', {
                params: { registration_id: regId, type: 'conference', per_page: 20 }
            });
            const raw = res.data?.data || res.data || [];
            const certs = (Array.isArray(raw) ? raw : []).map((c: any) => ({
                id: c.id,
                label: c.role || c.certificate_type?.name || 'Sertifikat',
                is_file_generated: !!c.is_file_generated,
                url: c.signed_file_url || c.local_file_url || null,
            }));
            setPreviewCerts(certs);
            const firstAvailable = certs.find((c) => c.is_file_generated && c.url);
            setSelectedPreviewCertId(firstAvailable?.id ?? null);
        } catch (e) {
            setPreviewCerts([]);
            setSelectedPreviewCertId(null);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleScoreChange = async (id: number, field: 'paper_score' | 'presenter_score' | 'notes', val: string, originalVal: any) => {
        const updateVal = field === 'notes' ? val : (val === "" ? null : parseFloat(val));
        if (updateVal === originalVal) return; // no change
        const key = `${field}-${id}`;
        setLoadingStates(prev => ({ ...prev, [key]: true }));
        try {
            await updateScores.mutateAsync({ id, payload: { [field]: updateVal } });
            toast.success(field === 'notes' ? "Catatan disimpan" : "Skor berhasil disimpan");
        } catch (error) {
            toast.error(field === 'notes' ? "Gagal menyimpan catatan" : "Gagal menyimpan skor");
        } finally {
            setLoadingStates(prev => ({ ...prev, [key]: false }));
        }
    };

    const handleToggle = async (type: 'presence' | 'best_presenter', id: number, currentVal: boolean) => {
        const key = `${type}-${id}`;
        setLoadingStates(prev => ({ ...prev, [key]: true }));
        try {
            if (type === 'presence') {
                await togglePresence.mutateAsync({ id, is_present: !currentVal });
                toast.success("Status kehadiran diperbarui");
            } else if (type === 'best_presenter') {
                await toggleBestPresenter.mutateAsync({ id, is_best_presenter: !currentVal });
                toast.success("Status Best Presenter diperbarui");
            }
        } catch (error) {
            toast.error("Gagal memperbarui status");
        } finally {
            setLoadingStates(prev => ({ ...prev, [key]: false }));
        }
    };

    const handleBestPaperChange = async (id: number, rank: number | null) => {
        const key = `best_paper-${id}`;
        setLoadingStates(prev => ({ ...prev, [key]: true }));
        try {
            await toggleBestPaper.mutateAsync({ id, best_paper_rank: rank });
            toast.success("Status Best Paper diperbarui");
        } catch (error) {
            toast.error("Gagal memperbarui status");
        } finally {
            setLoadingStates(prev => ({ ...prev, [key]: false }));
        }
    };

    const toggleRow = (id: number) => {
        const newSet = new Set(expandedRows);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setExpandedRows(newSet);
    };

    const filteredAttendances = useMemo(() => {
        if (!attendances) return [];
        return attendances.filter((att) => {
            let matchesSearch = true;
            if (filterSearch) {
                const s = filterSearch.toLowerCase();
                const r = att.registration;
                matchesSearch = !!(r?.participant_name?.toLowerCase().includes(s) ||
                    r?.institution_name?.toLowerCase().includes(s) ||
                    r?.submission_title?.toLowerCase().includes(s));
            }

            let matchesPresence = true;
            if (filterPresence === "present") matchesPresence = att.is_present === true;
            if (filterPresence === "absent") matchesPresence = att.is_present === false;

            let matchesStatus = true;
            if (filterStatus === "scored") matchesStatus = !!((att.paper_score != null && att.paper_score > 0) || (att.presenter_score != null && att.presenter_score > 0));
            if (filterStatus === "unscored") matchesStatus = !((att.paper_score != null && att.paper_score > 0) || (att.presenter_score != null && att.presenter_score > 0));

            let matchesAchievement = true;
            if (filterAchievement === "best_paper") matchesAchievement = att.is_best_paper === true;
            if (filterAchievement === "best_paper_1") matchesAchievement = att.best_paper_rank === 1;
            if (filterAchievement === "best_paper_2") matchesAchievement = att.best_paper_rank === 2;
            if (filterAchievement === "best_paper_3") matchesAchievement = att.best_paper_rank === 3;
            if (filterAchievement === "best_presenter") matchesAchievement = att.is_best_presenter === true;

            let matchesPeriod = true;
            if (filterPeriod !== "all") matchesPeriod = String(att.room?.period_id) === filterPeriod;

            let matchesDate = true;
            if (filterDate !== "all") matchesDate = att.room?.date === filterDate;

            return matchesSearch && matchesPresence && matchesStatus && matchesAchievement && matchesPeriod && matchesDate;
        });
    }, [attendances, filterSearch, filterPresence, filterStatus, filterAchievement, filterPeriod, filterDate]);

    const totalItems = filteredAttendances.length;
    const totalPages = Math.ceil(totalItems / perPage);
    const startIndex = (page - 1) * perPage;
    const endIndex = Math.min(startIndex + perPage, totalItems);
    const currentAttendances = filteredAttendances.slice(startIndex, endIndex);

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col xl:flex-row xl:items-center justify-between gap-6 relative overflow-hidden">
                <div className="absolute -top-12 -right-12 p-12 opacity-5 pointer-events-none">
                    <MapPin className="w-64 h-64 text-emerald-600" />
                </div>

                <div className="flex items-start gap-5 relative z-10">
                    <div className="p-4 rounded-3xl bg-emerald-50 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400 flex-shrink-0 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
                        <MapPin className="w-8 h-8" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">
                            Presensi Ruangan
                        </h1>
                        <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl">
                            Pilih periode, track konferensi, tanggal, dan ruangan untuk melihat daftar presenter yang ter-plot. Anda dapat menandai kehadiran serta menominasikan Best Paper / Best Presenter.
                        </p>
                    </div>
                </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
                <div className="p-5 md:p-6 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-200/70 dark:border-slate-800 flex flex-col gap-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <Select value={filterPeriod} onValueChange={(v) => { setFilterPeriod(v); setPage(1); }}>
                            <SelectTrigger className="w-full !h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl">
                                <SelectValue placeholder="Pilih Periode" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                                <SelectItem value="all">Semua Periode</SelectItem>
                                {periods?.map((period: any) => (
                                    <SelectItem key={period.period_id} value={String(period.period_id)}>
                                        {period.name} {period.is_active && "(Aktif)"}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select value={filterConference} onValueChange={(v) => { setFilterConference(v); setPage(1); }}>
                            <SelectTrigger className="w-full !h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl">
                                <SelectValue placeholder="Pilih Track Konferensi" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl w-[var(--radix-select-trigger-width)] max-h-[300px]">
                                <SelectItem value="all">Semua Track</SelectItem>
                                {conferences?.map((conf: any) => (
                                    <SelectItem key={conf.conference_id} value={String(conf.conference_id)} className="whitespace-normal break-words">
                                        {conf.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select value={filterDate} onValueChange={(v) => { setFilterDate(v); setPage(1); }}>
                            <SelectTrigger className="w-full !h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl">
                                <SelectValue placeholder="Pilih Tanggal" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                                <SelectItem value="all">Semua Tanggal</SelectItem>
                                {uniqueDates.map((date: any) => (
                                    <SelectItem key={date} value={date}>{date}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select value={filterRoom} onValueChange={(v) => { setFilterRoom(v); setPage(1); }}>
                            <SelectTrigger className="w-full !h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl">
                                <SelectValue placeholder="Pilih Ruangan" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl w-[var(--radix-select-trigger-width)] max-h-[300px]">
                                {!isPetugasPresensi && <SelectItem value="all">-- Pilih Ruangan --</SelectItem>}
                                {availableRooms.map((r: ConferenceRoom) => (
                                    <SelectItem key={r.id} value={String(r.id)} className="whitespace-normal break-words">
                                        {r.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Advanced Filters */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-slate-200/60 dark:border-slate-800/60 pt-4">
                        <Select value={filterPresence} onValueChange={(v) => { setFilterPresence(v); setPage(1); }}>
                            <SelectTrigger className="w-full !h-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-sm">
                                <SelectValue placeholder="Status Kehadiran" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                                <SelectItem value="all">Semua Kehadiran</SelectItem>
                                <SelectItem value="present">Hadir</SelectItem>
                                <SelectItem value="absent">Tidak Hadir</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(1); }}>
                            <SelectTrigger className="w-full !h-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-sm">
                                <SelectValue placeholder="Status Penilaian" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                                <SelectItem value="all">Semua Status Nilai</SelectItem>
                                <SelectItem value="scored">Sudah Dinilai</SelectItem>
                                <SelectItem value="unscored">Belum Dinilai</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={filterAchievement} onValueChange={(v) => { setFilterAchievement(v); setPage(1); }}>
                            <SelectTrigger className="w-full !h-10 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-sm">
                                <SelectValue placeholder="Status Pencapaian" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl">
                                <SelectItem value="all">Semua Pencapaian</SelectItem>
                                <SelectItem value="best_paper">Best Paper (Semua Juara)</SelectItem>
                                <SelectItem value="best_paper_1">Best Paper - Juara 1</SelectItem>
                                <SelectItem value="best_paper_2">Best Paper - Juara 2</SelectItem>
                                <SelectItem value="best_paper_3">Best Paper - Juara 3</SelectItem>
                                <SelectItem value="best_presenter">Best Presenter</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
                        <div className="bg-white dark:bg-slate-800 rounded-xl p-3 border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col justify-center">
                            <span className="text-xs text-slate-500 font-medium">Total Partisipan</span>
                            <span className="text-xl font-bold text-slate-800 dark:text-slate-100">{stats.total}</span>
                        </div>
                        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 border border-emerald-100 dark:border-emerald-800/30 shadow-sm flex flex-col justify-center">
                            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Hadir</span>
                            <span className="text-xl font-bold text-emerald-700 dark:text-emerald-300">
                                {stats.present} <span className="text-xs font-normal">({stats.total ? Math.round((stats.present/stats.total)*100) : 0}%)</span>
                            </span>
                        </div>
                        <div className="bg-rose-50 dark:bg-rose-900/20 rounded-xl p-3 border border-rose-100 dark:border-rose-800/30 shadow-sm flex flex-col justify-center">
                            <span className="text-xs text-rose-600 dark:text-rose-400 font-medium">Tidak Hadir</span>
                            <span className="text-xl font-bold text-rose-700 dark:text-rose-300">
                                {stats.absent} <span className="text-xs font-normal">({stats.total ? Math.round((stats.absent/stats.total)*100) : 0}%)</span>
                            </span>
                        </div>
                        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 border border-amber-100 dark:border-amber-800/30 shadow-sm flex flex-col justify-center">
                            <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Belum Dinilai</span>
                            <span className="text-xl font-bold text-amber-700 dark:text-amber-300">{stats.unscored}</span>
                        </div>
                    </div>

                    {/* Missing Winners Alert */}
                    {missingWinners && (
                        <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800 mt-2">
                            <TriangleAlert className="h-4 w-4 text-amber-600 dark:text-amber-500" />
                            <AlertTitle className="text-amber-800 dark:text-amber-400 font-semibold text-sm">Perhatian</AlertTitle>
                            <AlertDescription className="text-amber-700 dark:text-amber-500 text-xs mt-1">
                                Terdapat ruangan atau track yang belum menentukan Juara Makalah (Best Paper) atau Best Presenter. Gunakan tombol "Hitung Juara Otomatis" untuk menyelesaikannya.
                            </AlertDescription>
                        </Alert>
                    )}

                    <div className="relative w-full">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-slate-400" />
                        </div>
                        <Input
                            type="text"
                            placeholder="Cari nama, institusi, atau judul paper..."
                            value={filterSearch}
                            onChange={(e) => { setFilterSearch(e.target.value); setPage(1); }}
                            className="pl-10 h-11 w-full bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl"
                            disabled={filterRoom === "all" && filterConference === "all"}
                        />
                    </div>



                </div>

                <Tabs defaultValue="daftar" className="w-full">
                    <div className="px-5 md:px-6 pb-4 flex flex-col md:flex-row md:justify-between md:items-end pt-2 gap-4">
                        <TabsList className="bg-slate-100/80 dark:bg-slate-800/80 p-1 h-auto">
                            <TabsTrigger value="daftar" className="px-4 py-2 text-sm font-semibold data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:text-slate-800 dark:data-[state=active]:text-white rounded-md shadow-sm">
                                Daftar Partisipan
                            </TabsTrigger>
                            <TabsTrigger value="resume" className="px-4 py-2 text-sm font-semibold data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700 data-[state=active]:text-slate-800 dark:data-[state=active]:text-white rounded-md shadow-sm">
                                Resume
                            </TabsTrigger>
                        </TabsList>
                        <div className="flex items-center gap-2">
                        <Button
                            onClick={handleExportExcel}
                            size="sm"
                            disabled={isExporting}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm dark:bg-emerald-600 dark:hover:bg-emerald-700 dark:text-white"
                        >
                            {isExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                            Export Excel
                        </Button>
                        <Button
                            onClick={() => setIsAutoRankModalOpen(true)}
                            size="sm"
                            disabled={autoRankBestPaper.isPending || (filterRoom === "all" && !isPrivilegedRole)}
                            title={filterRoom === "all" && !isPrivilegedRole ? "Hanya Administrator dan Operator Conference yang dapat menghitung Best Paper track ini" : undefined}
                            className={filterRoom === "all" ? "bg-amber-500 hover:bg-amber-600 text-white shadow-sm" : "bg-blue-600 hover:bg-blue-700 text-white shadow-sm dark:bg-blue-600 dark:hover:bg-blue-700"}
                        >
                                                        {autoRankBestPaper.isPending || autoRankBestPresenter.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trophy className="w-4 h-4 mr-2" />}
                            {filterRoom === "all" ? "Hitung Best Paper Otomatis" : "Hitung Best Presenter Otomatis"}
                        </Button>
                        <Button
                            onClick={() => setIsGenerateModalOpen(true)}
                            size="sm"
                            disabled={isGenerating || filterConference === "all" || (filterRoom === "all" && !isPrivilegedRole)}
                            title={filterRoom === "all" && !isPrivilegedRole ? "Hanya Administrator dan Operator Conference yang dapat generate sertifikat track ini" : undefined}
                            className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm dark:bg-violet-600 dark:hover:bg-violet-700"
                        >
                            {isGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                            {filterRoom !== "all" ? "Generate Sertifikat Room Ini" : "Generate Sertifikat Track Ini"}
                        </Button>
                    </div>
                </div>

                <TabsContent value="daftar" className="m-0 outline-none">
                    <div className="overflow-x-auto border-t border-slate-200 dark:border-slate-800">
                        <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-sm">
                                <th className="px-4 py-4 font-semibold w-12 text-center"></th>
                                <th className="px-4 py-4 font-semibold w-16 text-center">No</th>
                                <th className="px-6 py-4 font-semibold">Detail Partisipan</th>
                                <th className="px-6 py-4 font-semibold text-center w-36">Best Paper</th>
                                <th className="px-6 py-4 font-semibold text-center w-36">Best Presenter</th>
                                <th className="px-6 py-4 font-semibold text-left w-48">Catatan Tambahan</th>
                                <th className="px-6 py-4 font-semibold text-center w-32 sticky right-0 bg-slate-50/80 dark:bg-slate-800/50 backdrop-blur-sm z-10 border-l border-slate-200 dark:border-slate-800 shadow-[-4px_0_10px_rgba(0,0,0,0.02)]">
                                    Kehadiran
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                            {filterRoom === "all" && filterConference === "all" ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-16 text-center text-slate-500">
                                        <MapPin className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
                                        <p className="text-lg font-medium text-slate-600 dark:text-slate-400">Silakan pilih Track Konferensi atau Ruangan terlebih dahulu.</p>
                                    </td>
                                </tr>
                            ) : isAttendancesLoading ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-emerald-500" />
                                        <p>Memuat data presensi...</p>
                                    </td>
                                </tr>
                            ) : currentAttendances.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-16 text-center text-slate-500">
                                        <MapPin className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
                                        <p className="text-lg font-medium text-slate-600 dark:text-slate-400">Tidak ada partisipan ter-plot pada filter ini.</p>
                                    </td>
                                </tr>
                            ) : (
                                currentAttendances.map((att, index) => {
                                    const isExpanded = expandedRows.has(att.id);
                                    return (
                                        <React.Fragment key={att.id}>
                                            <tr
                                                className={`hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors cursor-pointer ${isExpanded ? 'bg-slate-50/50 dark:bg-slate-800/20' : ''}`}
                                                onClick={() => toggleRow(att.id)}
                                            >
                                                <td className="px-4 py-4 text-center text-slate-400 align-top">
                                                    {isExpanded ? <ChevronUp className="w-5 h-5 mx-auto" /> : <ChevronDown className="w-5 h-5 mx-auto" />}
                                                </td>
                                                <td className="px-4 py-4 text-center font-medium text-slate-500 dark:text-slate-400 align-top">
                                                    {startIndex + index + 1}
                                                </td>
                                                <td className="px-6 py-4 align-top">
                                                    <div className="font-bold text-slate-900 dark:text-white mb-1 flex items-center flex-wrap gap-2">
                                                          {att.registration?.participant_name || "Unknown Participant"}
                                                          
                                                      </div>
                                                    <div className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                                                        {att.registration?.institution_name || "-"}
                                                    </div>
                                                    
                                                    <div className="flex flex-col gap-1 mb-2">
                                                        {att.registration?.email && (
                                                            <div className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                                                                <Mail className="w-3 h-3" />
                                                                <span>{att.registration.email}</span>
                                                            </div>
                                                        )}
                                                        {att.registration?.whatsapp_number && (
                                                            <a
                                                                href={`https://wa.me/${att.registration.whatsapp_number.replace(/\D/g, '').replace(/^0/, '62')}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 hover:underline transition-colors"
                                                            >
                                                                <Phone className="w-3 h-3" />
                                                                <span>{att.registration.whatsapp_number}</span>
                                                            </a>
                                                        )}
                                                    </div>

                                                    {att.room && (
                                                        <div className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-800">
                                                            {att.room.name}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 align-top" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex flex-col gap-2.5 min-w-[120px]">
                                                        <div className="relative">
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                max="5"
                                                                placeholder="Skor"
                                                                disabled={!att.is_present}
                                                                className="w-full h-8 text-xs pl-3 pr-14 rounded-lg border bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-50 dark:disabled:bg-slate-900/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                                defaultValue={att.paper_score || ""}
                                                                onBlur={(e) => handleScoreChange(att.id, 'paper_score', e.target.value, att.paper_score)}
                                                            />
                                                            <div className="absolute right-1.5 top-0 h-full flex items-center gap-1.5 pointer-events-none">
                                                                {(() => {
                                                                    const isTied = att.paper_score && att.paper_score > 0 && (scoreCounts.get(att.paper_score) || 0) > 1;
                                                                    return isTied ? (
                                                                        <span className="text-[9px] text-amber-600 dark:text-amber-500 font-bold bg-amber-50 dark:bg-amber-900/30 px-1 py-0.5 rounded flex items-center border border-amber-200 dark:border-amber-800 shadow-sm">
                                                                            Seri!
                                                                        </span>
                                                                    ) : null;
                                                                })()}
                                                                {loadingStates[`paper_score-${att.id}`] && (
                                                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-500" />
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="relative h-8">
                                                            {loadingStates[`best_paper-${att.id}`] ? (
                                                                <div className="flex justify-center items-center h-full w-full"><Loader2 className="w-5 h-5 animate-spin text-emerald-500" /></div>
                                                            ) : (
                                                                <Select
                                                                    value={att.best_paper_rank ? String(att.best_paper_rank) : "0"}
                                                                    onValueChange={(val) => {
                                                                        const rank = val === "0" ? null : parseInt(val);
                                                                        handleBestPaperChange(att.id, rank);
                                                                    }}
                                                                    disabled={!att.is_present || toggleBestPaper.isPending}
                                                                >
                                                                    <SelectTrigger className="w-full h-8 text-xs bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 focus:ring-emerald-500 rounded-lg">
                                                                        <SelectValue placeholder="Status Pemenang" />
                                                                    </SelectTrigger>
                                                                    <SelectContent className="rounded-lg border-slate-200 dark:border-slate-800">
                                                                        <SelectItem value="0" className="text-slate-500 font-medium cursor-pointer focus:bg-slate-50 dark:focus:bg-slate-800/50">- Bukan Juara -</SelectItem>
                                                                        <SelectItem value="1" className="font-bold text-amber-600 dark:text-amber-500 cursor-pointer focus:bg-amber-50 dark:focus:bg-amber-500/10 focus:text-amber-700 dark:focus:text-amber-400">Juara 1</SelectItem>
                                                                        <SelectItem value="2" className="font-bold text-slate-500 dark:text-slate-400 cursor-pointer focus:bg-slate-50 dark:focus:bg-slate-400/10 focus:text-slate-700 dark:focus:text-slate-300">Juara 2</SelectItem>
                                                                        <SelectItem value="3" className="font-bold text-amber-700/80 dark:text-amber-700 cursor-pointer focus:bg-orange-50 dark:focus:bg-orange-900/20 focus:text-amber-800 dark:focus:text-amber-600">Juara 3</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 align-top" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex flex-col gap-2.5 min-w-[120px]">
                                                        <div className="relative w-full">
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                max="5"
                                                                placeholder="Skor"
                                                                disabled={!att.is_present}
                                                                className="w-full h-8 text-xs pl-3 pr-14 rounded-lg border bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-50 dark:disabled:bg-slate-900/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                                defaultValue={att.presenter_score || ""}
                                                                onBlur={(e) => handleScoreChange(att.id, 'presenter_score', e.target.value, att.presenter_score)}
                                                            />
                                                            <div className="absolute right-1.5 top-0 h-full flex items-center gap-1.5 pointer-events-none">
                                                                {(() => {
                                                                    const key = `${att.room_id}_${att.presenter_score}`;
                                                                    const isTied = att.room_id && att.presenter_score && att.presenter_score > 0 && (presenterScoreCounts.get(key) || 0) > 1;
                                                                    return isTied ? (
                                                                        <span className="text-[9px] text-amber-600 dark:text-amber-500 font-bold bg-amber-50 dark:bg-amber-900/30 px-1 py-0.5 rounded flex items-center border border-amber-200 dark:border-amber-800 shadow-sm">
                                                                            Seri!
                                                                        </span>
                                                                    ) : null;
                                                                })()}
                                                                {loadingStates[`presenter_score-${att.id}`] && (
                                                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-500" />
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center justify-between gap-1.5 w-full h-8 px-2 rounded-lg border bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 transition-all hover:border-slate-300 dark:hover:border-slate-700">
                                                            {loadingStates[`best_presenter-${att.id}`] ? (
                                                                <Loader2 className="w-4 h-4 animate-spin text-emerald-500 flex-shrink-0" />
                                                            ) : (
                                                                <Switch
                                                                    checked={att.is_best_presenter}
                                                                    onCheckedChange={() => handleToggle('best_presenter', att.id, att.is_best_presenter)}
                                                                    disabled={!att.is_present || toggleBestPresenter.isPending}
                                                                    className="data-[state=checked]:bg-emerald-500 scale-90 origin-left"
                                                                />
                                                            )}
                                                            <span className="text-[10px] text-slate-600 dark:text-slate-400 font-bold whitespace-nowrap">Juara Presenter</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 align-top" onClick={(e) => e.stopPropagation()}>
                                                    <div className="relative w-full">
                                                        <textarea
                                                            placeholder="Ketik catatan..."
                                                            className="w-full min-h-[72px] text-xs p-3 rounded-lg border bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all resize-none leading-relaxed"
                                                            defaultValue={att.notes || ""}
                                                            onBlur={(e) => handleScoreChange(att.id, 'notes', e.target.value, att.notes || "")}
                                                        />
                                                        {loadingStates[`notes-${att.id}`] && (
                                                            <Loader2 className="absolute right-2 top-2 w-4 h-4 animate-spin text-emerald-500" />
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-center align-top sticky right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm z-10 border-l border-slate-100 dark:border-slate-800/60 shadow-[-4px_0_10px_rgba(0,0,0,0.02)]" onClick={(e) => e.stopPropagation()}>
                                                    {(() => {
                                                        const generatedCount = att.registration?.certificates?.filter((c) => c.is_file_generated).length ?? 0;
                                                        const isCertGenerated = generatedCount > 0;
                                                        return (
                                                            <TooltipProvider delayDuration={150}>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <span className="mb-2 block w-full">
                                                                            <button
                                                                                onClick={() => isCertGenerated && handlePreviewCertificate(att)}
                                                                                disabled={!isCertGenerated}
                                                                                className={`flex items-center justify-center gap-1 w-full text-xs transition-colors ${isCertGenerated
                                                                                    ? "text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 hover:underline cursor-pointer"
                                                                                    : "text-slate-400 dark:text-slate-600 cursor-not-allowed"
                                                                                    }`}
                                                                            >
                                                                                <Eye className="w-3.5 h-3.5" />
                                                                                <span>{isCertGenerated ? "Sertifikat" : "Belum Ada"}</span>
                                                                            </button>
                                                                        </span>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent side="top">
                                                                        {isCertGenerated ? (
                                                                            <span>{generatedCount} sertifikat sudah digenerate. Klik untuk preview.</span>
                                                                        ) : (
                                                                            <span>Sertifikat belum digenerate. Gunakan tombol "Generate Sertifikat" di atas terlebih dahulu.</span>
                                                                        )}
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        );
                                                    })()}
                                                    {loadingStates[`presence-${att.id}`] ? (
                                                        <Loader2 className="w-5 h-5 animate-spin mx-auto text-emerald-500" />
                                                    ) : (
                                                        <div className="flex flex-col items-center justify-center gap-1.5">
                                                            <Switch
                                                                checked={att.is_present}
                                                                onCheckedChange={() => handleToggle('presence', att.id, att.is_present)}
                                                                disabled={togglePresence.isPending}
                                                                className="data-[state=checked]:bg-emerald-500"
                                                            />
                                                            {att.is_present && <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Hadir</span>}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr className="bg-slate-50/50 dark:bg-slate-800/20 border-b border-slate-100 dark:border-slate-800/60">
                                                    <td colSpan={7} className="px-6 py-5">
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-12 pr-4">
                                                            <div className="space-y-3">
                                                                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Detail Registrasi</h4>
                                                                <div className="flex items-start gap-3 text-sm">
                                                                    <Hash className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                                                                    <div>
                                                                        <span className="text-slate-500 block text-xs">ID Registrasi</span>
                                                                        <span className="font-medium text-slate-700 dark:text-slate-300">REG-{att.registration_id.toString().padStart(5, '0')}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-start gap-3 text-sm">
                                                                    <Building2 className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                                                                    <div>
                                                                        <span className="text-slate-500 block text-xs">Institusi</span>
                                                                        <span className="font-medium text-slate-700 dark:text-slate-300">{att.registration?.institution_name || "-"}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-start gap-3 text-sm">
                                                                    <PackageIcon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                                                                    <div>
                                                                        <span className="text-slate-500 block text-xs">Paket Dipilih</span>
                                                                        <span className="font-medium text-slate-700 dark:text-slate-300">{att.registration?.package?.name || "-"}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="space-y-3">
                                                                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Informasi Paper</h4>
                                                                {att.registration?.submission_title ? (
                                                                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4 shadow-sm">
                                                                        <h5 className="font-bold text-slate-800 dark:text-slate-200 leading-tight mb-2">
                                                                            {att.registration.submission_title}
                                                                        </h5>
                                                                        {att.registration.submission_authors && (
                                                                            <p className="text-sm text-slate-500 dark:text-slate-400">
                                                                                <span className="font-medium">Authors:</span> {att.registration.submission_authors}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <div className="bg-slate-100 dark:bg-slate-800/50 rounded-xl p-4 border border-slate-200 border-dashed dark:border-slate-700 text-center">
                                                                        <span className="text-sm text-slate-500 italic">Peserta ini tidak memiliki/mengajukan paper.</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {totalItems > 0 && !isAttendancesLoading && (filterRoom !== "all" || filterConference !== "all") && (
                    <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-3">
                            <span className="text-slate-500 font-medium text-xs sm:text-sm">
                                Menampilkan <span className="font-semibold text-slate-700 dark:text-slate-300">{startIndex + 1} - {endIndex}</span> dari <span className="font-semibold text-slate-700 dark:text-slate-300">{totalItems}</span> data
                            </span>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                            <div className="flex items-center gap-2">
                                <span className="text-slate-500 text-xs sm:text-sm font-medium">Per halaman:</span>
                                <select
                                    value={perPage}
                                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                    className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500 font-bold text-slate-700 dark:text-slate-300 text-xs sm:text-sm shadow-sm"
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="h-8 px-3 rounded-lg border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 font-medium"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" />
                                    <span className="hidden sm:inline">Prev</span>
                                </Button>
                                <span className="text-slate-600 dark:text-slate-400 font-medium text-xs sm:text-sm min-w-[3rem] text-center">
                                    {page} / {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages || totalPages === 0}
                                    className="h-8 px-3 rounded-lg border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 font-medium"
                                >
                                    <span className="hidden sm:inline">Next</span>
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
                </TabsContent>

                <TabsContent value="resume" className="m-0 border-t border-slate-200 dark:border-slate-800 p-5 md:p-6 bg-slate-50/50 dark:bg-slate-900/20 outline-none">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div className="bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/20 dark:to-slate-900 rounded-3xl p-8 border border-amber-200/60 dark:border-amber-800/50 flex items-center justify-between shadow-sm hover:shadow-md transition-all group">
                            <div>
                                <h4 className="font-bold text-amber-700/80 dark:text-amber-500/80 mb-2 uppercase tracking-wider text-xs">Total Best Paper</h4>
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mb-1">Pada Track Ini</p>
                                <div className="text-5xl font-black text-amber-600 dark:text-amber-400 mt-2">
                                    {trackAttendances?.filter((a: any) => a.is_best_paper).length || 0}
                                </div>
                            </div>
                            <div className="w-24 h-24 rounded-full bg-amber-100/50 dark:bg-amber-900/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                <Trophy className="w-12 h-12 text-amber-500" />
                            </div>
                        </div>
                        <div className="bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-900 rounded-3xl p-8 border border-emerald-200/60 dark:border-emerald-800/50 flex items-center justify-between shadow-sm hover:shadow-md transition-all group">
                            <div>
                                <h4 className="font-bold text-emerald-700/80 dark:text-emerald-500/80 mb-2 uppercase tracking-wider text-xs">Total Best Presenter</h4>
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mb-1">Pada Track Ini</p>
                                <div className="text-5xl font-black text-emerald-600 dark:text-emerald-400 mt-2">
                                    {trackAttendances?.filter((a: any) => a.is_best_presenter).length || 0}
                                </div>
                            </div>
                            <div className="w-24 h-24 rounded-full bg-emerald-100/50 dark:bg-emerald-900/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                <Medal className="w-12 h-12 text-emerald-500" />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-8">
                        {/* Best Paper Section */}
                        <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm">
                            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100 dark:border-slate-800">
                                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                                    <Trophy className="w-5 h-5 text-amber-600 dark:text-amber-500" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-800 dark:text-white">Pemenang Best Paper</h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400">Peringkat 1, 2, dan 3 tingkat Track Konferensi</p>
                                </div>
                            </div>
                            
                            {trackAttendances?.filter((a: any) => a.is_best_paper).length === 0 ? (
                                <div className="text-center py-8 text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
                                    Belum ada pemenang Best Paper yang terpilih.
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {trackAttendances?.filter((a: any) => a.is_best_paper).sort((a: any, b: any) => (a.best_paper_rank || 99) - (b.best_paper_rank || 99)).map((p: any) => (
                                        <div key={p.id} className="relative bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/10 dark:to-slate-900 rounded-xl p-5 border border-amber-200/60 dark:border-amber-800/50 flex flex-col shadow-sm hover:shadow-md transition-shadow">
                                            <div className="absolute top-0 right-0 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-bold px-4 py-1.5 rounded-bl-2xl rounded-tr-xl flex items-center gap-1.5 shadow-sm">
                                                <Trophy className="w-3.5 h-3.5" /> Juara {p.best_paper_rank}
                                            </div>
                                            <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center mb-3 border border-amber-200 dark:border-amber-700/50 shadow-inner">
                                                <span className="text-amber-600 dark:text-amber-400 font-black text-xl">{p.best_paper_rank}</span>
                                            </div>
                                            <div className="font-bold text-slate-800 dark:text-white text-lg mb-1">{p.registration?.participant_name || "Nama tidak tersedia"}</div>
                                            <div className="text-sm text-slate-500 dark:text-slate-400 line-clamp-1 mb-3 flex items-center gap-1.5">
                                                <Building2 className="w-3.5 h-3.5" /> {p.registration?.institution_name || "Institusi tidak tersedia"}
                                            </div>
                                            <div className="bg-amber-100/50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-200/50 dark:border-amber-800/30 mt-auto">
                                                <div className="text-xs font-semibold text-amber-800 dark:text-amber-400 mb-1 flex items-center gap-1">
                                                    <PackageIcon className="w-3.5 h-3.5" /> Judul Paper
                                                </div>
                                                <div className="text-xs font-medium text-slate-600 dark:text-slate-300 line-clamp-2 leading-relaxed">{p.registration?.submission_title || "Tidak ada judul paper"}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Best Presenter Section */}
                        <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm">
                            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100 dark:border-slate-800">
                                <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                    <Trophy className="w-5 h-5 text-emerald-600 dark:text-emerald-500" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-slate-800 dark:text-white">Pemenang Best Presenter</h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400">Pemenang presentasi terbaik dari setiap ruangan</p>
                                </div>
                            </div>

                            {trackAttendances?.filter((a: any) => a.is_best_presenter).length === 0 ? (
                                <div className="text-center py-8 text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
                                    Belum ada pemenang Best Presenter yang terpilih.
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {Array.from(new Set(trackAttendances?.filter((a: any) => a.is_best_presenter).map((a: any) => a.room?.name || 'Tidak ada ruangan'))).sort().map(roomName => (
                                        <div key={roomName as string} className="bg-slate-50 dark:bg-slate-800/30 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                                            <div className="bg-slate-100 dark:bg-slate-800 px-4 py-2 font-semibold text-slate-700 dark:text-slate-300 border-b border-slate-200 dark:border-slate-700 flex items-center">
                                                <MapPin className="w-4 h-4 mr-2 text-emerald-500" />
                                                {roomName as string}
                                            </div>
                                            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {trackAttendances?.filter((a: any) => a.is_best_presenter && (a.room?.name || 'Tidak ada ruangan') === roomName).map((p: any) => (
                                                    <div key={p.id} className="bg-white dark:bg-slate-900 rounded-xl p-4 border border-emerald-100 dark:border-emerald-800/40 shadow-sm flex items-start gap-4 hover:shadow-md transition-shadow relative overflow-hidden">
                                                        <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-50 dark:bg-emerald-900/20 rounded-bl-full -mr-8 -mt-8 z-0"></div>
                                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500 flex-shrink-0 flex items-center justify-center border-2 border-white dark:border-slate-800 shadow-sm z-10">
                                                            <Trophy className="w-5 h-5 text-white" />
                                                        </div>
                                                        <div className="flex-1 min-w-0 z-10">
                                                            <div className="font-bold text-slate-800 dark:text-white text-base truncate mb-0.5">{p.registration?.participant_name || "Nama tidak tersedia"}</div>
                                                            <div className="text-xs text-slate-500 dark:text-slate-400 mb-2 truncate flex items-center gap-1">
                                                                <Building2 className="w-3 h-3" /> {p.registration?.institution_name || "Institusi tidak tersedia"}
                                                            </div>
                                                            <div className="text-xs font-medium bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2.5 py-1.5 rounded-md border border-emerald-100 dark:border-emerald-800/50 line-clamp-2">
                                                                {p.registration?.submission_title || "Tidak ada judul paper"}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </TabsContent>
                </Tabs>
            </div>



            {/* Generate Certificate Modal */}
            <Dialog open={isGenerateModalOpen} onOpenChange={(open) => { if (!isGenerating) { setIsGenerateModalOpen(open); if (!open) setGenerateOutput(null); } }}>
                <DialogContent className="sm:max-w-[500px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-violet-700 dark:text-violet-400">
                            <FileText className="w-5 h-5" />
                            {filterRoom !== "all" ? "Generate Sertifikat Room Ini" : "Generate Sertifikat Track Ini"}
                        </DialogTitle>
                        <DialogDescription className="text-slate-500 dark:text-slate-400 mt-1">
                            {filterRoom !== "all"
                                ? "Akan men-generate sertifikat untuk semua peserta yang hadir di room ini (Presenter & Best Presenter). Sertifikat yang sudah ada sebelumnya tidak akan ditimpa."
                                : "Akan men-generate sertifikat untuk semua peserta yang hadir di track ini (Non-Presenter & Best Paper). Sertifikat yang sudah ada sebelumnya tidak akan ditimpa."}
                        </DialogDescription>
                    </DialogHeader>
                    {generateOutput && (
                        <div className="mt-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-mono text-slate-600 dark:text-slate-300 max-h-40 overflow-y-auto whitespace-pre-wrap">
                            {generateOutput}
                        </div>
                    )}
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => { setIsGenerateModalOpen(false); setGenerateOutput(null); }} disabled={isGenerating}>
                            {generateOutput ? "Tutup" : "Batal"}
                        </Button>
                        {!generateOutput && (
                            <Button
                                onClick={handleGenerateCertificates}
                                disabled={isGenerating}
                                className="bg-violet-600 hover:bg-violet-700 text-white"
                            >
                                {isGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                                {isGenerating ? "Sedang Memproses..." : "Ya, Generate Sekarang"}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Preview Certificate Modal */}
            {(() => {
                const selectedCert = previewCerts.find((c) => c.id === selectedPreviewCertId) || null;
                return (
                    <Dialog open={isPreviewModalOpen} onOpenChange={setIsPreviewModalOpen}>
                        <DialogContent className="sm:max-w-[800px] max-h-[90vh] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2 text-indigo-700 dark:text-indigo-400">
                                    <Eye className="w-5 h-5" />
                                    Preview Sertifikat — {previewName}
                                </DialogTitle>
                                {!previewLoading && previewCerts.length > 1 && (
                                    <DialogDescription className="text-slate-500 dark:text-slate-400">
                                        Peserta ini memiliki {previewCerts.length} sertifikat konferensi. Pilih salah satu di bawah untuk melihat isinya.
                                    </DialogDescription>
                                )}
                            </DialogHeader>

                            {/* Selector sertifikat, hanya tampil kalau peserta punya >1 sertifikat
                                (mis. Pemakalah + Juara Best Paper) supaya jelas mana yang sedang dilihat */}
                            {!previewLoading && previewCerts.length > 1 && (
                                <div className="flex flex-wrap gap-2 pb-1">
                                    {previewCerts.map((c) => (
                                        <button
                                            key={c.id}
                                            type="button"
                                            disabled={!c.is_file_generated || !c.url}
                                            onClick={() => setSelectedPreviewCertId(c.id)}
                                            className={cn(
                                                "px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors",
                                                selectedPreviewCertId === c.id
                                                    ? "bg-indigo-600 border-indigo-600 text-white"
                                                    : c.is_file_generated && c.url
                                                        ? "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-indigo-400"
                                                        : "bg-slate-100 dark:bg-slate-800/50 border-slate-200 dark:border-slate-800 text-slate-400 cursor-not-allowed"
                                            )}
                                            title={!c.is_file_generated ? "Belum digenerate" : undefined}
                                        >
                                            {c.label}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className="w-full" style={{ height: '65vh' }}>
                                {previewLoading ? (
                                    <div className="flex items-center justify-center h-full">
                                        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                                        <span className="ml-3 text-slate-500">Memuat sertifikat...</span>
                                    </div>
                                ) : selectedCert?.url ? (
                                    <iframe
                                        src={selectedCert.url}
                                        className="w-full h-full rounded-xl border border-slate-200 dark:border-slate-700"
                                        title={`Preview Sertifikat - ${selectedCert.label}`}
                                    />
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-3">
                                        <FileText className="w-12 h-12 opacity-30" />
                                        <p className="text-sm font-medium">Sertifikat belum di-generate</p>
                                        <p className="text-xs text-slate-400">Klik tombol "Generate Sertifikat" terlebih dahulu</p>
                                    </div>
                                )}
                            </div>
                            {selectedCert?.url && (
                                <div className="flex items-center justify-between pt-2">
                                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                                        {selectedCert.label}
                                    </span>
                                    <a href={selectedCert.url} target="_blank" rel="noreferrer"
                                        className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 underline font-medium"
                                    >
                                        <Download className="w-4 h-4" />
                                        Buka / Unduh PDF
                                    </a>
                                </div>
                            )}
                        </DialogContent>
                    </Dialog>
                );
            })()}

            <Dialog open={isAutoRankModalOpen} onOpenChange={setIsAutoRankModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{filterRoom === "all" ? "Hitung Best Paper Otomatis" : "Hitung Best Presenter Otomatis"}</DialogTitle>
                        <DialogDescription>
                            {filterRoom === "all" 
                                ? "Apakah Anda yakin ingin menghitung juara Best Paper secara otomatis berdasarkan skor paper? Ini akan mereset status Best Paper sebelumnya di seluruh track ini."
                                : "Apakah Anda yakin ingin menghitung juara Best Presenter secara otomatis berdasarkan skor presenter? Ini akan mereset status Best Presenter sebelumnya di ruangan ini."}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsAutoRankModalOpen(false)} disabled={autoRankBestPaper.isPending || autoRankBestPresenter.isPending}>
                            Batal
                        </Button>
                        <Button className={filterRoom === "all" ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-blue-600 hover:bg-blue-700 text-white dark:bg-blue-600 dark:hover:bg-blue-700"} onClick={handleAutoRank} disabled={autoRankBestPaper.isPending || autoRankBestPresenter.isPending}>
                            {(autoRankBestPaper.isPending || autoRankBestPresenter.isPending) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            Ya, Hitung
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Winners Modal */}
            <Dialog open={isWinnersModalOpen} onOpenChange={setIsWinnersModalOpen}>
                <DialogContent className="sm:max-w-[600px] rounded-3xl p-0 overflow-hidden border-0 shadow-2xl">
                    <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-6 text-center text-white relative">
                        <Trophy className="w-16 h-16 mx-auto mb-4 text-emerald-100 opacity-90 drop-shadow-md" />
                        <DialogTitle className="text-2xl font-black tracking-tight mb-1">
                            {filterRoom === "all" ? "Pemenang Best Paper" : "Pemenang Best Presenter"}
                        </DialogTitle>
                        <p className="text-emerald-100 font-medium">
                            Telah berhasil dikalkulasi secara otomatis
                        </p>
                    </div>
                    <div className="p-6 max-h-[60vh] overflow-y-auto bg-slate-50 dark:bg-slate-900/50">
                        <div className="space-y-3">
                            {winnersData.map((winner, idx) => (
                                <div key={idx} className="bg-white dark:bg-slate-800 rounded-2xl p-4 border border-slate-200 dark:border-slate-700 shadow-sm flex items-center justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-bold text-slate-900 dark:text-white truncate">
                                            {winner.registration?.participant_name}
                                        </h3>
                                        <p className="text-sm text-slate-500 truncate mb-1">
                                            {winner.registration?.institution_name}
                                        </p>
                                        <div className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400">
                                            Skor: {filterRoom === "all" ? winner.paper_score : winner.presenter_score}
                                        </div>
                                    </div>
                                    {filterRoom === "all" && (
                                        <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl bg-amber-100 text-amber-600 font-black text-xl border border-amber-200 shadow-inner">
                                            #{winner.best_paper_rank}
                                        </div>
                                    )}
                                    <div className="flex-shrink-0 ml-2">
                                        <Button variant="outline" size="sm" onClick={() => handleToggleWinner(winner)} className="h-8 text-xs px-2 text-rose-500 hover:text-rose-600 hover:bg-rose-50 border-rose-200">
                                            Batalkan
                                        </Button>
                                    </div>
                                </div>
                            ))}
                            {winnersData.length === 0 && (
                                <div className="text-center py-8 text-slate-500">
                                    Tidak ada pemenang ditemukan.
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex justify-end">
                        <Button onClick={() => setIsWinnersModalOpen(false)} className="rounded-xl px-6">
                            Tutup
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}







